import * as React from 'react'

import { classNames } from 'src/utils/classNames'

import { type VariantProps, cva } from 'class-variance-authority'

import Spinner from './Spinner'

const buttonVariants = cva(
  'flex items-center font-headline rounded-full focus:outline-none justify-center text-sm tracking-wide h-max disabled:opacity-50 disabled:cursor-default duration-100',
  {
    variants: {
      variant: {
        primary: 'bg-th-primary text-th-white',
        secondary: 'bg-th-content-secondary',
        default: 'bg-th-content text-th-text hover:bg-th-content-secondary',
        error: 'bg-th-error text-th-white'
      },
      bordered: {
        true: 'border border-th-border',
        false: ''
      },
      size: {
        small: 'py-1 px-2 text-xs',
        medium: 'py-2 px-4 text-sm',
        large: 'py-3 px-6 text-lg'
      }
    },
    defaultVariants: {
      variant: 'default',
      bordered: false,
      size: 'medium'
    }
  }
)

const spinnerVariants = cva('mr-2', {
  variants: {
    size: {
      small: 'h-3 w-3',
      medium: 'h-4 w-4',
      large: 'h-5 w-5'
    }
  },
  defaultVariants: {
    size: 'medium'
  }
})

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  'aria-label'?: string
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, bordered, children, 'aria-label': ariaLabel, isLoading, size, ...props }, ref) => {
    const disabled = props.disabled || isLoading
    return (
      <button
        {...props}
        className={classNames(buttonVariants({ variant, bordered, className, size }))}
        ref={ref}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        disabled={disabled}
      >
        {isLoading && <Spinner className={classNames(spinnerVariants({ size }), variant == 'primary' && '!border-l-th-content')} />}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
export default Button
